import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Subject, interval, takeUntil, takeWhile } from 'rxjs';

import { SigningService } from './signing.service';
import { UserService } from '../../../../shared/service/user.service';
import { Severity, ToastService } from '../../../../shared/components/toast/toast.service';
import { AppSubject } from '../../../../shared/app-subject';
import { validateAllFormFields } from '../../../../shared/components/validator/validator.service';

@Component({
  selector: 'app-signing',
  templateUrl: './signing.component.html'
})
export class SigningComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private isInternalClosing = false;

  submitting = false;
  pluginNotFound = false;
  isTypeSelectionVisible = false;
  isCtsSelectionVisible = false;
  isRemoteWaitVisible = false;
  isSigningProcessVisible = false;
  displayPinCode = false;

  signingTypes: Array<any> = [];
  ctsList: any[] = [];
  items: Array<any> = [];
  selectedType: any;
  selectedCTS: any;

  signingData = {
    partyOtherIndentId: '',
    idValue: '',
    idValues: '',
    partyClassificationId: '',
    signingType: '',
    pinCode: '',
  };

  form = this.fb.group({
    pinCode: ['', Validators.required],
  });

  timeLeft = 180;
  minutes = 3;
  seconds = 0;

  constructor(
    private signingService: SigningService,
    private fb: FormBuilder,
    private toastService: ToastService
  ) {
  }

  ngOnInit(): void {
    this.resetSigningState();
    this.initializeSigningTypes();
    this.subscribeToServiceEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancel(): void {
    if (this.isInternalClosing) {
      this.isInternalClosing = false;
      return;
    }
    this.signingService.cancel();
    this.resetSigningState();
  }

  submit(): void {
    if (this.submitting) {
      return;
    }

    if (!this.selectedType && this.signingTypes.length > 0) {
      this.selectedType = this.signingTypes[0].value;
    }
    UserService.set(UserService.KEY.SIGNING, this.selectedType);
    this.signingData.partyClassificationId = this.selectedType;

    const runValue = JSON.parse(UserService.get(UserService.KEY.RUN_VALUE));
    this.signingService.run(runValue).finally(() => {
      this.submitting = false;
    });

    this.isInternalClosing = true;
    this.isTypeSelectionVisible = false;
  }

  submit2(): void {
    if (this.submitting) {
      return;
    }

    if (this.displayPinCode && !validateAllFormFields(this.form)) {
      return;
    }

    this.submitting = true;
    this.isInternalClosing = true;
    this.isCtsSelectionVisible = false;
    this.populateSigningData();

    if (this.displayPinCode || this.selectedType === 'IntrustRemoteSigning') {
      this.isSigningProcessVisible = true;
    } else {
      this.isRemoteWaitVisible = true;
      this.startTimer();
    }

    this.signingService.remoteSigning(this.signingData).subscribe({
      next: () => {
        this.submitting = false;
        this.handleSigningSuccess();
      },
      error: (error) => {
        this.submitting = false;
        this.handleSigningError(error);
      }
    });
  }

  onCTSChange(selectedValue: any): void {
    const id = typeof selectedValue === 'object' ? selectedValue.value : selectedValue;
    this.selectedCTS = id;
    const selected = this.ctsList.find(item => item.partyOtherIndentId === id);
    this.displayPinCode = selected?.requirePinCode === 'Y';
  }

  typeSelect(event: any): void {
    this.selectedType = event.value;
  }

  private resetSigningState(): void {
    UserService.set(UserService.KEY.SIGNING, '');
    UserService.set(UserService.KEY.REMOTE_SIGNING, '');
    this.isTypeSelectionVisible = false;
    this.isCtsSelectionVisible = false;
    this.isRemoteWaitVisible = false;
    this.isSigningProcessVisible = false;
    this.pluginNotFound = false;
    this.displayPinCode = false;
    this.submitting = false;
    this.isInternalClosing = false;
    this.form.reset();
    this.timeLeft = 180;
    this.minutes = 3;
    this.seconds = 0;
  }

  private initializeSigningTypes(): void {
    const signingTypesRaw = UserService.get(UserService.KEY.SIGNING_TYPES);
    if (signingTypesRaw && signingTypesRaw !== 'NULL') {
      try {
        const arr = JSON.parse(signingTypesRaw);
        this.signingTypes = arr.map((item: any) => ({
          value: item.partyClassificationId,
          label: item.classificationDesc || item.description || item.partyClassificationId
        }));
      } catch (e) {
        this.signingTypes = [];
      }
    }
  }

  private subscribeToServiceEvents(): void {
    this.signingService.errorSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: any) => {
        if (error && (error.status === 0 || error?.name === 'TimeoutError')) {
          this.pluginNotFound = true;
        }
      });

    this.signingService.displaySubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => this.handleDisplayChange(res));
  }

  private handleDisplayChange(res: string): void {
    switch (res) {
      case 'form1':
        this.initializeSigningTypes();
        this.isTypeSelectionVisible = true;
        this.pluginNotFound = false;
        break;
      case 'form2':
        this.setupCtsSelection();
        this.isInternalClosing = true;
        this.isTypeSelectionVisible = false;
        this.isCtsSelectionVisible = true;
        break;
      case 'form3':
        this.isSigningProcessVisible = true;
        break;
      case 'closeForm3':
        this.isSigningProcessVisible = false;
        break;
    }
  }

  private setupCtsSelection(): void {
    const remoteSigningRaw = UserService.get(UserService.KEY.REMOTE_SIGNING);
    if (remoteSigningRaw) {
      try {
        const arr = JSON.parse(remoteSigningRaw);
        this.ctsList = arr;
        if (arr && arr.length > 0) {
          this.selectedCTS = arr[0].partyOtherIndentId;
          this.items = arr.map((item: any) => ({
            value: item.partyOtherIndentId,
            label: item.idValue
          }));
          this.displayPinCode = arr[0]?.requirePinCode === 'Y';
        }
      } catch (e) {
        this.ctsList = [];
      }
    }
  }

  private populateSigningData(): void {
    const runValue = JSON.parse(UserService.get(UserService.KEY.RUN_VALUE));
    this.signingData.pinCode = this.displayPinCode ? (this.form.value.pinCode || '') : '';
    this.signingData.partyOtherIndentId = typeof this.selectedCTS === 'object' ? this.selectedCTS.value : this.selectedCTS;
    this.signingData.idValue = runValue?.idValue;
    this.signingData.idValues = runValue?.idValues;
    this.signingData.signingType = runValue?.actionType;
    this.signingData.partyClassificationId = this.selectedType || UserService.get(UserService.KEY.SIGNING);
  }

  private handleSigningSuccess(): void {
    this.toastService.success();
    this.resetSigningState();
    AppSubject.setReloadData('success');
  }

  private handleSigningError(error: any): void {
    this.toastService.setMessage({ severity: Severity.ERROR, detail: error.error?.errors || error.error?.errorMessage });
    this.resetSigningState();
    AppSubject.setReloadData('error');
  }

  private startTimer(): void {
    this.timeLeft = 180;
    this.minutes = 3;
    this.seconds = 0;

    interval(1000)
      .pipe(
        takeWhile(() => this.timeLeft > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.timeLeft--;
        this.minutes = Math.floor(this.timeLeft / 60);
        this.seconds = this.timeLeft % 60;
      });
  }
}
